import Input from '@ixigo/iui/Input';
import Typography from '@ixigo/iui/Typography';
import { useState } from 'react';
import type { BaseContactDetail } from '@/api';
import { SectionHeader } from '../section-header';
import type { ContactDetailBaseProps } from './ContactDetail';
import {
	validateEmail,
	validateFullName,
	validatePhoneNumber as validatePhoneHelper,
} from './helper';
import type { ValidationResult } from './index.types';
import { MobileNumberInput, type MobileNumberWithISD } from './MobileInput';

export interface ContactDetailEditProps extends ContactDetailBaseProps {
	initialDetails?: Partial<BaseContactDetail>;
	onChange?: (details: BaseContactDetail) => void;
}

type InputChange =
	| { field: 'fullName'; value: string }
	| { field: 'email'; value: string }
	| { field: 'mobileInfo'; value: MobileNumberWithISD };

export function ContactDetailEdit({
	title = 'Contact Details',
	subtitle,
	initialDetails = {},
	onChange,
}: ContactDetailEditProps) {
	const [contactDetails, setContactDetails] = useState<BaseContactDetail>({
		userName: initialDetails.userName || '',
		email: initialDetails.email || '',
		mobileInfo: {
			number: initialDetails.mobileInfo?.number || '',
			isdInfo: initialDetails.mobileInfo?.isdInfo || {
				code: 'in',
				number: '91',
				name: 'India',
			},
		},
	});

	const [errors, setErrors] = useState<{
		fullName?: string;
		email?: string;
		mobileNumber?: string;
	}>({});

	const validateMobileNumber = async (
		mobileInfo: MobileNumberWithISD,
	): Promise<ValidationResult> => {
		if (!mobileInfo.number) {
			return { valid: false, error: 'Mobile number is required' };
		}

		const isValid = await validatePhoneHelper(
			mobileInfo.number,
			mobileInfo.isdInfo,
		);
		if (!isValid) {
			return { valid: false, error: 'Please enter a valid mobile number' };
		}
		return { valid: true, error: null };
	};

	const handleInputChange = async (change: InputChange) => {
		let updatedDetails = { ...contactDetails };

		if (change.field === 'fullName') {
			updatedDetails = { ...updatedDetails, userName: change.value };
			setErrors({ ...errors, fullName: undefined });
		} else if (change.field === 'email') {
			updatedDetails = { ...updatedDetails, email: change.value };
			setErrors({ ...errors, email: undefined });
		} else if (change.field === 'mobileInfo') {
			updatedDetails = { ...updatedDetails, mobileInfo: change.value };
			setErrors({ ...errors, mobileNumber: undefined });
		}

		setContactDetails(updatedDetails);
		onChange?.(updatedDetails);
	};

	const handleBlur = async (field: string) => {
		const newErrors = { ...errors };

		if (field === 'fullName') {
			const fullNameValidation = validateFullName(contactDetails.userName);
			if (!fullNameValidation.valid) {
				newErrors.fullName = fullNameValidation.error;
			} else {
				delete newErrors.fullName;
			}
		}

		if (field === 'email') {
			const emailValidation = await validateEmail(contactDetails.email);
			if (!emailValidation.valid) {
				newErrors.email = emailValidation.error;
			} else {
				delete newErrors.email;
			}
		}

		if (field === 'mobileInfo') {
			const mobileData = contactDetails.mobileInfo;
			delete newErrors.mobileNumber;

			if (mobileData.number) {
				const mobileValidation = await validateMobileNumber(mobileData);
				if (!mobileValidation.valid) {
					newErrors.mobileNumber = mobileValidation.error;
				}
			}
		}
		setErrors(newErrors);
	};

	return (
		<div
			className="bg-primary p-20 flex flex-col gap-[15px]"
			id="contact-detail"
		>
			<div className="flex flex-col">
				<SectionHeader title={title} />
				<Typography variant="body-sm" className="text-secondary">
					{subtitle}
				</Typography>
			</div>

			<div className="flex flex-col gap-[15px]">
				<Input
					type="text"
					label="Full Name"
					variant="outlined"
					value={contactDetails.userName}
					onChange={(e) =>
						handleInputChange({ field: 'fullName', value: e.target.value })
					}
					placeholder="Enter your full name"
					required
					maxLength={15}
					validationState={errors.fullName ? 'error' : undefined}
					helperText={errors.fullName}
					onBlur={() => handleBlur('fullName')}
				/>

				<Input
					type="email"
					label="Email"
					variant="outlined"
					value={contactDetails.email}
					onChange={(e) =>
						handleInputChange({ field: 'email', value: e.target.value })
					}
					placeholder="Enter your email"
					required
					maxLength={254}
					validationState={errors.email ? 'error' : undefined}
					helperText={errors.email}
					onBlur={() => handleBlur('email')}
				/>

				<MobileNumberInput
					label="Mobile Number"
					value={contactDetails.mobileInfo}
					onChange={(v) => handleInputChange({ field: 'mobileInfo', value: v })}
					placeholder="Enter mobile number"
					validationState={errors.mobileNumber ? 'error' : undefined}
					helperText={errors.mobileNumber}
					required
					onBlur={() => handleBlur('mobileInfo')}
				/>
			</div>
		</div>
	);
}
